import React, { useCallback, useEffect, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionGroup,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  CircularProgress,
  Divider,
  Grid,
  Sheet,
  Stack,
  Table,
  Typography,
} from '@mui/joy';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toFreeAgentBillAppUrl } from '../../../freeagent/appUrl';
import { CIS_BAND_LABELS, type DirectoryAccount } from '../accounts.model';
import { accountsService } from '../accounts.service';
import { VAT_TREATMENT_LABELS, formatGbp } from '../arrangements.model';
import { BILL_LINE_KIND_LABELS, allowedBillActions, type AccountBill } from '../bills.model';
import { billsService, type BillParties } from '../bills.service';
import { BillActionsMenu } from '../components/BillActionsMenu';
import { BillFormModal, MarkPaidModal, SendBillModal, TotalsBreakdown, useBillToCompanies } from '../components/BillForms';
import { BillStatusChip } from './BillsPage';

function Fact({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <Stack spacing={0.25}>
      <Typography level="body-xs" textColor="neutral.500" textTransform="uppercase" letterSpacing="0.04em">{label}</Typography>
      <Typography level="body-sm" component="div">{children}</Typography>
    </Stack>
  );
}

function ukDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const [year, month, day] = iso.slice(0, 10).split('-');
  return day ? `${day}/${month}/${year}` : iso;
}

/** One bill: what they invoiced, what we pay, and where it stands in FreeAgent. */
const BillPage: React.FC = () => {
  const { billId = '' } = useParams<{ billId: string }>();
  const navigate = useNavigate();
  const [bill, setBill] = useState<AccountBill | null>(null);
  const [parties, setParties] = useState<BillParties | null>(null);
  const [accounts, setAccounts] = useState<DirectoryAccount[]>([]);
  const billToCompanies = useBillToCompanies();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<{ tone: 'success' | 'danger'; text: string } | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [sending, setSending] = useState<AccountBill | null>(null);
  const [paying, setPaying] = useState<AccountBill | null>(null);

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    setError(null);
    try {
      const next = await billsService.get(billId);
      if (!next) throw new Error('Bill not found');
      const [nextParties, accountRows] = await Promise.all([billsService.parties(next), accountsService.list()]);
      setBill(next);
      setParties(nextParties);
      setAccounts(accountRows);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load the bill');
    } finally {
      setLoading(false);
    }
  }, [billId]);

  useEffect(() => {
    void load();
  }, [load]);

  const changed = (next: AccountBill) => {
    setBill(next);
    void load(true);
  };

  const run = async (label: string, action: () => Promise<string | void>) => {
    setBusy(label);
    setMessage(null);
    try {
      const text = await action();
      if (text) setMessage({ tone: 'success', text });
      await load(true);
    } catch (err) {
      setMessage({ tone: 'danger', text: err instanceof Error ? err.message : String((err as any)?.message || err) });
    } finally {
      setBusy(null);
    }
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;
  }
  if (error || !bill) {
    return (
      <Box sx={{ p: 3 }}>
        <Button component={Link} to="/bills" size="sm" variant="plain" color="neutral" sx={{ ml: -1, mb: 2 }}>← Bills</Button>
        <Alert color="danger">{error || 'Bill not found'}</Alert>
      </Box>
    );
  }

  const allowed = allowedBillActions(bill);
  const account = parties?.account || null;
  const billTo = parties?.billTo || null;
  const showQuantities = bill.lines.some((line) => line.quantity != null || line.unitPrice != null);

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1080 }}>
      <Stack spacing={2.5}>
        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ ml: -1 }}>
          <Button component={Link} to="/bills" size="sm" variant="plain" color="neutral">Bills</Button>
          <Typography level="body-sm" textColor="neutral.400">›</Typography>
          <Typography level="body-sm" textColor="neutral.600">{account?.companyName || 'Bill'}</Typography>
        </Stack>

        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1.5}>
          <Stack spacing={0.5}>
            <Typography level="body-sm" textColor="neutral.500" fontWeight="lg">Bill from {account?.companyName || 'unknown account'}</Typography>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
              <Typography level="h3">{bill.reference}</Typography>
              <BillStatusChip bill={bill} />
            </Stack>
          </Stack>
          <Stack direction="row" spacing={1} alignItems="flex-start" flexWrap="wrap">
            <Button variant="outlined" color="neutral" loading={busy === 'pdf'} onClick={() => run('pdf', () => billsService.downloadPdf(bill))}>
              Download PDF
            </Button>
            {allowed.edit && <Button variant="outlined" onClick={() => setEditing(true)}>Edit</Button>}
            {allowed.send && <Button onClick={() => setSending(bill)}>Send to FreeAgent</Button>}
            {allowed.updateFreeAgent && <Button color="warning" onClick={() => setSending(bill)}>Update FreeAgent</Button>}
            {allowed.markPaid && <Button color="success" variant="soft" onClick={() => setPaying(bill)}>Mark as paid</Button>}
            <BillActionsMenu
              hideOpen
              bill={bill}
              onEdit={() => setEditing(true)}
              onSend={setSending}
              onMarkPaid={setPaying}
              onChanged={changed}
              onDeleted={() => navigate('/bills')}
            />
          </Stack>
        </Stack>

        {message && <Alert color={message.tone}>{message.text}</Alert>}
        {bill.isLegacy && (
          <Alert color="neutral" variant="soft">
            Copied from the old subcontractor invoices and kept exactly as recorded. It is already in FreeAgent as a subcontractor payment, so it is not sent again.
          </Alert>
        )}
        {bill.syncError && <Alert color="danger">{bill.syncError}</Alert>}

        <Card variant="outlined">
          <Grid container spacing={2}>
            <Grid xs={12} sm={6} md={3}>
              <Fact label="From">
                {account ? <Link to="/directory">{account.companyName}</Link> : String(bill.legacySnapshot?.issued_by || 'Unknown account')}
                {account?.utrNumber && <Typography level="body-xs" textColor="neutral.500" display="block">UTR {account.utrNumber}</Typography>}
                {account?.cisVerificationNumber && (
                  <Typography level="body-xs" textColor="neutral.500" display="block">Verification {account.cisVerificationNumber}</Typography>
                )}
              </Fact>
            </Grid>
            <Grid xs={12} sm={6} md={3}>
              <Fact label="Bill to">
                {billTo?.name || '—'}
                {billTo?.address && <Typography level="body-xs" textColor="neutral.500" display="block" sx={{ whiteSpace: 'pre-line' }}>{billTo.address}</Typography>}
              </Fact>
            </Grid>
            <Grid xs={6} md={2}><Fact label="Invoice date">{ukDate(bill.datedOn)}</Fact></Grid>
            <Grid xs={6} md={2}><Fact label="Due">{ukDate(bill.dueOn)}</Fact></Grid>
            <Grid xs={6} md={2}><Fact label="Paid">{ukDate(bill.paidOn)}</Fact></Grid>
            <Grid xs={6} md={3}><Fact label="CIS">{bill.isLegacy && bill.totals.cisRate != null ? `${Math.round(bill.totals.cisRate * 1000) / 10}%` : CIS_BAND_LABELS[bill.cisBand]}</Fact></Grid>
            <Grid xs={6} md={3}><Fact label="VAT">{VAT_TREATMENT_LABELS[bill.vatTreatment]}</Fact></Grid>
            {bill.description && <Grid xs={12} md={6}><Fact label="Description">{bill.description}</Fact></Grid>}
          </Grid>
        </Card>

        <Sheet variant="outlined" sx={{ borderRadius: 'sm', overflow: 'auto' }}>
          <Table>
            <thead>
              <tr>
                <th>Description</th>
                <th style={{ width: 110 }}>Type</th>
                {showQuantities && <th style={{ width: 70, textAlign: 'right' }}>Qty</th>}
                {showQuantities && <th style={{ width: 110, textAlign: 'right' }}>Unit</th>}
                <th style={{ width: 140, textAlign: 'right' }}>Amount (ex VAT)</th>
              </tr>
            </thead>
            <tbody>
              {bill.lines.map((line, index) => (
                <tr key={index}>
                  <td>{line.description || '—'}</td>
                  <td>{BILL_LINE_KIND_LABELS[line.kind]}</td>
                  {showQuantities && <td style={{ textAlign: 'right' }}>{line.quantity ?? ''}</td>}
                  {showQuantities && <td style={{ textAlign: 'right' }}>{line.unitPrice != null ? formatGbp(line.unitPrice) : ''}</td>}
                  <td style={{ textAlign: 'right' }}>{formatGbp(line.net)}</td>
                </tr>
              ))}
            </tbody>
          </Table>
          <Divider />
          <Box sx={{ p: 1.5, display: 'flex', justifyContent: 'flex-end' }}>
            <Box sx={{ minWidth: 320 }}>
              <TotalsBreakdown totals={bill.totals} vatTreatment={bill.vatTreatment} legacy={bill.isLegacy} />
            </Box>
          </Box>
        </Sheet>

        {bill.notes && (
          <Card variant="soft">
            <Fact label="Notes"><Typography level="body-sm" sx={{ whiteSpace: 'pre-line' }}>{bill.notes}</Typography></Fact>
          </Card>
        )}

        {!bill.isLegacy && (
          <Card variant="outlined">
            <Stack spacing={1}>
              <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} spacing={1}>
                <Stack spacing={0.25}>
                  <Typography level="title-sm">FreeAgent</Typography>
                  <Typography level="body-sm" textColor="neutral.600">
                    {!bill.freeagentBillUrl
                      ? 'Not in FreeAgent yet.'
                      : bill.freeagentOutOfDate
                        ? 'Changed here since it was sent. Update FreeAgent so the two match.'
                        : `In FreeAgent${bill.lastSyncedAt ? `, checked ${ukDate(bill.lastSyncedAt)}` : ''}.`}
                  </Typography>
                </Stack>
                <Stack direction="row" spacing={1}>
                  {bill.freeagentBillUrl && (
                    <Button component="a" href={toFreeAgentBillAppUrl(bill.freeagentBillUrl)} target="_blank" rel="noopener" variant="outlined">
                      Open in FreeAgent
                    </Button>
                  )}
                  {allowed.refresh && (
                    <Button
                      variant="plain"
                      loading={busy === 'refresh'}
                      onClick={() => run('refresh', async () => { await billsService.refreshFromFreeAgent(bill.id); return 'Refreshed from FreeAgent.'; })}
                    >
                      Refresh
                    </Button>
                  )}
                </Stack>
              </Stack>
              {bill.cisBand !== 'none' && bill.freeagentBillUrl && (
                <Typography level="body-sm">
                  CIS deduction: {formatGbp(bill.totals.cisDeduction)} here,{' '}
                  {bill.freeagentCisDeduction != null ? `${formatGbp(bill.freeagentCisDeduction)} in FreeAgent` : 'not yet read from FreeAgent'}.
                </Typography>
              )}
              {bill.cisBand !== 'none' && (
                <Typography level="body-xs" textColor="neutral.600">
                  Pay {formatGbp(bill.totals.payableTotal)} and explain the bank transaction in FreeAgent as a <b>Bill Payment</b> against this bill, not as a subcontractor payment. FreeAgent then records the {formatGbp(bill.totals.cisDeduction)} CIS as owed to HMRC (811) and the full {formatGbp(bill.totals.netTotal)} as cost.
                </Typography>
              )}
            </Stack>
          </Card>
        )}

        {bill.legacySnapshot && (
          <AccordionGroup variant="outlined" sx={{ borderRadius: 'sm' }}>
            <Accordion>
              <AccordionSummary>Original record</AccordionSummary>
              <AccordionDetails>
                <Box component="pre" sx={{ m: 0, fontSize: 12, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                  {JSON.stringify(bill.legacySnapshot, null, 2)}
                </Box>
              </AccordionDetails>
            </Accordion>
          </AccordionGroup>
        )}
      </Stack>

      <BillFormModal
        open={editing}
        bill={bill}
        accounts={accounts}
        billToCompanies={billToCompanies}
        onAccountCreated={(created) => setAccounts((current) => [...current, created])}
        onClose={() => setEditing(false)}
        onSaved={(saved) => {
          setEditing(false);
          changed(saved);
          if (saved.freeagentOutOfDate) setSending(saved);
        }}
      />
      <SendBillModal
        bill={sending}
        onClose={() => setSending(null)}
        onSent={(sent) => {
          setSending(null);
          changed(sent);
          if (sent.status === 'sent') setMessage({ tone: 'success', text: 'FreeAgent is up to date.' });
        }}
      />
      <MarkPaidModal
        bill={paying}
        onClose={() => setPaying(null)}
        onSaved={(paid) => { setPaying(null); changed(paid); }}
      />
    </Box>
  );
};

export default BillPage;
